import { useCallback, useEffect, useRef } from 'react';
import useAutosizeTextArea from '@/hooks/useAutoTextArea';
import type { AIStrings } from '@/utils/filters/options/getFilterOptions';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';

// this removes the # before showing summary for editing
function removeHashTags(sentence: string): string {
  return sentence.replace(/#/g, '');
}

type Props = {
  editText: string;
  setEditText: (s: string) => void;
  setError: (e: { error: boolean; text: string }) => void;
  error: { error: boolean; text: string };
  updateQuery: () => void;
  strings: AIStrings;
  locale: Locale;
};

const MAX_CHARACTERS = 500;

/**
 * a custom text area component with animating border color and autosizing capability
 */
export default function TextArea({
  editText,
  setEditText,
  updateQuery,
  setError,
  error,
  strings,
  locale
}: Props): JSX.Element {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textAreaRef.current, editText);

  useEffect(() => {
    textAreaRef.current?.focus();
  }, []);

  const handleChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = evt.target?.value;
    const charCount = val.length;

    if (charCount > MAX_CHARACTERS) {
      return;
    }

    if (charCount > MAX_CHARACTERS - 50) {
      setError({
        error: true,
        text: formatMessage(strings.aiRemainingCount, locale, {
          characterCount: String(MAX_CHARACTERS - charCount)
        })
      });
    } else if (error.error) {
      setError({ error: false, text: '' });
    }

    setEditText(val);
    const container = document.getElementById('filters-search-container');

    function backToNormal(height: number | undefined) {
      if (height) {
        container?.style.setProperty('height', `${String(height)}px`);
      }
      textAreaRef.current?.style.setProperty('height', '22px');
    }

    if (container?.style.height !== 'fit-content') {
      const normalHeight = container?.offsetHeight;
      container?.style.setProperty('height', 'fit-content');

      setTimeout(() => {
        window.addEventListener(
          'scroll',
          () => {
            backToNormal(normalHeight);
          },
          { once: true }
        );
      }, 100);
    }
  };

  const onEnterSearch = useCallback(
    event => {
      // on enter key press -> search
      if (event.keyCode === 13) {
        event.preventDefault();
        updateQuery();
      }
    },
    [updateQuery]
  );

  return (
    <textarea
      placeholder={strings.searchPlaceholder}
      className="m-3 ml-6 mt-[18px] w-full resize-none text-b-md focus:outline-none lg:mt-[14px] lg:text-b-base"
      onChange={handleChange}
      ref={textAreaRef}
      rows={1}
      value={removeHashTags(editText)}
      onKeyDown={onEnterSearch}
    />
  );
}
